import { Box, SxProps, Theme } from '@mui/material';
import { ComponentType, forwardRef, MouseEventHandler } from 'react';
import type { IconBaseProps } from 'react-icons';

/**
 * A react-icons component (we standardise on Lucide — the `Lu*` exports from
 * `react-icons/lu`).
 */
export type AppIconType = ComponentType<IconBaseProps>;

type FontSizeToken = 'inherit' | 'small' | 'medium' | 'large';

export interface AppIconProps {
  icon: AppIconType;
  /** Explicit pixel size — overrides the `fontSize` token. */
  size?: number;
  /** MUI-style size token, for parity with `@mui/icons-material`. */
  fontSize?: FontSizeToken;
  /**
   * MUI palette path (e.g. `primary.main`, `error.main`, `text.secondary`) or
   * any CSS colour. Defaults to `currentColor` so the icon inherits its context.
   */
  color?: string;
  /** Lucide stroke width (default 2 — the canonical Lucide weight). */
  strokeWidth?: number;
  sx?: SxProps<Theme>;
  className?: string;
  onClick?: MouseEventHandler<HTMLSpanElement>;
  title?: string;
}

const SIZE_TOKENS: Record<FontSizeToken, number | string> = {
  inherit: '1em',
  small: 20,
  medium: 24,
  large: 30,
};

/**
 * Renders a Lucide (react-icons) glyph with the ergonomics of an MUI icon.
 * Wrapping in a Box lets callers keep using `color`, `fontSize` and `sx`, so
 * swapping `@mui/icons-material` imports for Lucide is a mechanical, low-risk
 * change that stays theme-aware in light and dark mode.
 */
const AppIcon = forwardRef<HTMLSpanElement, AppIconProps>(
  (
    {
      icon: Icon,
      size,
      fontSize = 'medium',
      color = 'currentColor',
      strokeWidth = 2,
      sx,
      className,
      onClick,
      title,
      // Spread the rest so MUI can pass through tooltip/aria props (e.g. when
      // used directly as a <Tooltip> child) without dropping them.
      ...rest
    },
    ref
  ) => {
    const resolved = size ?? SIZE_TOKENS[fontSize];

    return (
      <Box
        ref={ref}
        component='span'
        className={className}
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          lineHeight: 0,
          color,
          ...sx,
        }}
        {...rest}
      >
        <Icon size={resolved} strokeWidth={strokeWidth} title={title} aria-hidden />
      </Box>
    );
  }
);

AppIcon.displayName = 'AppIcon';

export default AppIcon;
